/**
 * Owner activation / product registration (/activate).
 *
 * Different from the batch authenticity check (/verify): this registers the
 * owner and collects first-party answers about why they bought.
 *
 * ACTIVATION_FORM_ENDPOINT · where the form POSTs its answers as JSON
 * (e.g. a Shopify Flow webhook, Klaviyo form endpoint, Zapier/Make hook or
 * your own server). While it's empty NOTHING is stored: the page says so
 * honestly and offers to send the answers by email instead.
 *
 * ACTIVATION_SUCCESS_URL · optional page to send the owner to after a real,
 * successful submission. Empty = show the built-in success screen.
 */
/**
 * FormSubmit (formsubmit.co) emails every activation to support@gritvuegear.com.
 * The very first submission triggers a one-time confirmation email from FormSubmit
 * to that inbox; click "Activate Form" in it, and every activation after that arrives.
 */
export const ACTIVATION_FORM_ENDPOINT = 'https://formsubmit.co/ajax/support@gritvuegear.com'

/** Readable email body for FormSubmit (field names become the rows of the email). */
export function activationPayload(answers: ActivationAnswers): Record<string, string> {
  return {
    _subject: `New X1 activation · Order ${answers.orderNumber}`,
    _replyto: answers.email,
    _template: 'table',
    _captcha: 'false',
    'Order number': answers.orderNumber,
    Email: answers.email,
    'Uses X1 for': answers.useCase || '—',
    'Profession / trade': answers.trade || '—',
    'Problem to solve': answers.problem || '—',
    'Feature that mattered most': answers.feature || '—',
    'Heard about GritVue from': answers.heardFrom || '—',
    'Would recommend to a coworker': answers.recommend || '—',
    'Wears prescription lenses': answers.wearsPrescription || '—',
    'Submitted at': new Date().toISOString(),
  }
}
export const ACTIVATION_SUCCESS_URL = ''

/**
 * Printed pieces in the box. QR codes should point to your PUBLISHED site
 * domain + these paths. Empty values are not ready yet: don't print a QR for them.
 */
export const PRINT_QR_TARGETS = {
  /** Quick start guide QR · the step-by-step guide page. */
  QUICK_START_GUIDE_URL: '/guide',
  /** Order tracking page (opens USPS with the number filled in). */
  trackOrderPath: '/track',
  /** Activation card · "Make it official. Scan to activate." */
  activationCardPath: '/activate',
  /** Crew Rewards card · "Share GritVue. Get paid." */
  crewRewardsCardPath: '/rewards',
}

export const featureOptions = [
  'LED lighting',
  'Hands-free visibility',
  'Interchangeable lenses',
  'Prescription ready',
  'Warranty',
  'Work / jobsite use',
  'Other',
]

export const heardFromOptions = [
  'Facebook / Instagram',
  'TikTok',
  'YouTube',
  'Google search',
  'Friend or coworker',
  'Other',
]

export const recommendOptions = ['Yes', 'No', 'Not sure']
export const prescriptionOptions = ['Yes', 'No']

export interface ActivationAnswers {
  orderNumber: string
  email: string
  useCase: string
  trade: string
  problem: string
  feature: string
  heardFrom: string
  recommend: string
  wearsPrescription: string
}

export const emptyAnswers: ActivationAnswers = {
  orderNumber: '',
  email: '',
  useCase: '',
  trade: '',
  problem: '',
  feature: '',
  heardFrom: '',
  recommend: '',
  wearsPrescription: '',
}

/** Email fallback used only while ACTIVATION_FORM_ENDPOINT is empty. */
export function activationMailto(answers: ActivationAnswers): string {
  const lines = [
    `Order number: ${answers.orderNumber}`,
    `Email: ${answers.email}`,
    `Use: ${answers.useCase}`,
    `Trade: ${answers.trade}`,
    `Problem to solve: ${answers.problem}`,
    `Feature that mattered most: ${answers.feature}`,
    `Heard about GritVue from: ${answers.heardFrom}`,
    `Would recommend to a coworker: ${answers.recommend}`,
    `Wears prescription lenses: ${answers.wearsPrescription}`,
  ]
  return `mailto:support@gritvuegear.com?subject=${encodeURIComponent('Activate my GritVue X1')}&body=${encodeURIComponent(lines.join('\n'))}`
}
